import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, Download, Loader2, Search, Tags } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { StatTile } from '@/components/ui/stat-tile';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useBusinesses } from '@/features/business/use-businesses';
import { useDivisions } from '@/features/item-master/item-master.api';
import { MOVEMENT_REASON, MOVEMENT_TYPE } from '@/features/inventory/types';
import { formatCurrency, formatNumber } from '@/lib/format';
import { saveAsFile } from '@/lib/download';
import { cn } from '@/lib/utils';
import { storeToday } from '@/lib/store-time';
import {
  PAGE_SIZE,
  REPORT_SORT,
  useInventoryReport,
  useInventoryReportAll,
  type ReportQuery,
  type ReportRow,
} from './inventory-report.api';

/**
 * What moved, and what it was worth.
 *
 * The store Inventory page answers "what is on the shelf now" for one store;
 * this answers "what happened, and where did the value go" across the estate.
 * They share the movement enums, so the reason list here and the reason picker
 * there cannot disagree.
 *
 * The four movement totals lead, because the reason to open this report is
 * usually one of them — how much walked out as damage, how much came in as
 * receiving — and the original buried them under the table.
 */

const ANY = 'ANY';

/** Reasons a person can file a movement under, minus the "unspecified" one. */
const REASON_VALUES = Object.values(MOVEMENT_REASON);

export function InventoryReportPage() {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';

  const today = storeToday();
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [start, setStart] = useState(`${today.slice(0, 7)}-01`);
  const [end, setEnd] = useState(today);
  const [businessId, setBusinessId] = useState(ANY);
  const [type, setType] = useState(ANY);
  const [reasons, setReasons] = useState<string[]>([]);
  const [division, setDivision] = useState(ANY);
  const [sort, setSort] = useState(String(REPORT_SORT.default));
  const [page, setPage] = useState(0);

  const { data: businesses } = useBusinesses();
  const { data: divisions } = useDivisions();

  const params: ReportQuery = useMemo(
    () => ({
      query,
      businessId: businessId === ANY ? null : businessId,
      type: type === ANY ? null : Number(type),
      reasons: reasons.map(Number),
      divisionFilter: division === ANY ? null : division,
      sort: Number(sort),
      start,
      end,
    }),
    [query, businessId, type, reasons, division, sort, start, end]
  );

  const report = useInventoryReport(params, page);
  const exportAll = useInventoryReportAll();

  const rows = report.data?.report ?? [];
  const total = report.data?.total ?? 0;

  const reset = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const fields: FilterField[] = [
    {
      id: 'store',
      label: t('inventoryReport.store'),
      value: businessId,
      onChange: reset(setBusinessId),
      options: [
        { value: ANY, label: t('inventoryReport.allStores') },
        ...(businesses ?? []).map((business) => ({
          value: business.id,
          label: business.name,
        })),
      ],
    },
    {
      id: 'type',
      label: t('inventoryReport.movementType'),
      value: type,
      onChange: reset(setType),
      sort: false,
      resetValue: ANY,
      options: [
        { value: ANY, label: t('inventoryReport.anyType') },
        ...Object.values(MOVEMENT_TYPE).map((value) => ({
          value: String(value),
          label: t(`inventory.type${value}`),
        })),
      ],
    },
    {
      id: 'reasons',
      label: t('inventoryReport.reasons'),
      value: reasons.join(','),
      onChange: (value) => {
        setReasons(value ? value.split(',') : []);
        setPage(0);
      },
      resetValue: '',
      render: ({ id, disabled }) => (
        <MultiSelect
          id={id}
          icon={Tags}
          heading={t('inventoryReport.reasons')}
          allLabel={t('inventoryReport.anyReason')}
          summarise={(count) => t('inventoryReport.nReasons', { count })}
          disabled={disabled}
          selected={reasons}
          onChange={(next) => {
            setReasons(next);
            setPage(0);
          }}
          options={REASON_VALUES.map((value) => ({
            value: String(value),
            label: t(`inventory.reason${value}`),
          }))}
        />
      ),
    },
    {
      id: 'division',
      label: t('inventoryReport.division'),
      value: division,
      onChange: reset(setDivision),
      options: [
        { value: ANY, label: t('inventoryReport.anyDivision') },
        ...(divisions ?? []).map((name) => ({ value: name, label: name })),
      ],
    },
    {
      id: 'start',
      label: t('inventoryReport.from'),
      type: 'date',
      value: start,
      resetValue: start,
      onChange: reset(setStart),
    },
    {
      id: 'end',
      label: t('inventoryReport.to'),
      type: 'date',
      value: end,
      resetValue: end,
      onChange: reset(setEnd),
    },
    {
      id: 'sort',
      label: t('inventoryReport.sortBy'),
      value: sort,
      onChange: reset(setSort),
      sort: false,
      resetValue: String(REPORT_SORT.default),
      options: [
        { value: String(REPORT_SORT.default), label: t('inventoryReport.sortDefault') },
        { value: String(REPORT_SORT.amountMovedDesc), label: t('inventoryReport.sortMovedDesc') },
        { value: String(REPORT_SORT.amountMovedAsc), label: t('inventoryReport.sortMovedAsc') },
        { value: String(REPORT_SORT.valueMovedDesc), label: t('inventoryReport.sortValueDesc') },
        { value: String(REPORT_SORT.valueMovedAsc), label: t('inventoryReport.sortValueAsc') },
        { value: String(REPORT_SORT.quantityOnHandDesc), label: t('inventoryReport.sortQohDesc') },
        { value: String(REPORT_SORT.quantityOnHandAsc), label: t('inventoryReport.sortQohAsc') },
      ],
    },
  ];

  const rowName = (row: ReportRow) =>
    (isEnglish ? row.name : row.nameSpa) || row.name || row.nameSpa || '—';

  function runExport() {
    exportAll.mutate(params, {
      onSuccess: (all) => {
        if (!all?.length) {
          toast.info(t('inventoryReport.nothingToExport'));
          return;
        }
        const escape = (value: unknown) =>
          `"${String(value ?? '').replace(/"/g, '""')}"`;
        const header = [
          t('inventoryReport.gtin'),
          t('inventoryReport.product'),
          t('inventoryReport.moved'),
          t('inventoryReport.movedValue'),
          t('inventoryReport.onHand'),
          t('inventoryReport.onHandValue'),
        ];
        const csv = [
          header.join(','),
          ...all.map((row) =>
            [
              row.gtin,
              rowName(row),
              row.movementAmount,
              row.movementValue,
              row.totalQuantityOnHand,
              row.totalQuantityOnHandValue,
            ]
              .map(escape)
              .join(',')
          ),
        ].join('\n');
        saveAsFile(csv, `inventory-report-${start}-to-${end}.csv`, 'text/csv;charset=utf-8');
        toast.success(t('inventoryReport.exported', { count: all.length }));
      },
      onError: (error) =>
        toast.error(
          error instanceof Error
            ? error.message
            : t('inventoryReport.exportFailed')
        ),
    });
  }

  /**
   * The four movement types, each with its units and its money.
   *
   * Declared with their labels and tones up front so the tiles keep their
   * headings while the figures are still arriving — a row of four unlabelled
   * skeletons says nothing about what is loading.
   */
  const totals: {
    label: string;
    amount: number;
    value: number;
    tone: 'good' | 'cash' | 'warn' | 'bad';
  }[] = [
    {
      label: t('inventory.type3'),
      amount: report.data?.totalReceivingAmount ?? 0,
      value: report.data?.totalReceivingValue ?? 0,
      tone: 'good',
    },
    {
      label: t('inventory.type0'),
      amount: report.data?.totalSaleAmount ?? 0,
      value: report.data?.totalSaleValue ?? 0,
      tone: 'cash',
    },
    {
      label: t('inventory.type1'),
      amount: report.data?.totalAdjustmentUpAmount ?? 0,
      value: report.data?.totalAdjustmentUpValue ?? 0,
      tone: 'warn',
    },
    {
      label: t('inventory.type2'),
      amount: report.data?.totalAdjustmentDownAmount ?? 0,
      value: report.data?.totalAdjustmentDownValue ?? 0,
      tone: 'bad',
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('inventoryReport.title')}
        description={t('inventoryReport.subtitle')}
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={exportAll.isPending || rows.length === 0}
            onClick={runExport}
          >
            {exportAll.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              <Download className="size-3.5" />
            )}
            <span className="hidden sm:inline">
              {t('inventoryReport.export')}
            </span>
          </Button>
        }
      />

      <FilterBar
        busy={report.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void report.refetch();
              setQuery(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t('inventoryReport.searchPlaceholder')}
                aria-label={t('inventoryReport.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
        fields={fields}
        summary={
          report.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('inventoryReport.showingOf', { shown: rows.length, total })}
            </p>
          ) : null
        }
      />

      {/* ------------------------------------------------------- totals */}
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {totals.map((entry) => (
          <StatTile
            key={entry.label}
            label={entry.label}
            // Money leads on every tile: this sits under Finance and the
            // question it answers is what the movement was worth. The unit
            // count is the supporting detail, not the headline.
            value={formatCurrency(entry.value)}
            sub={t('inventoryReport.unitsMoved', {
              amount: formatNumber(entry.amount),
            })}
            tone={entry.tone}
            loading={report.isPending}
            busy={report.isFetching}
          />
        ))}
      </div>

      {report.isSuccess && (
        <p className="text-muted-foreground text-sm">
          {t('inventoryReport.onHandNow', {
            quantity: formatNumber(report.data.totalQuantityOnHand),
            value: formatCurrency(report.data.totalQuantityOnHandValue),
          })}
        </p>
      )}

      {report.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('inventoryReport.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void report.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {report.isPending && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-xl" />
          ))}
        </div>
      )}

      {report.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={report.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('inventoryReport.noResults')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="hidden rounded-xl border lg:block">
            <Table busy={report.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('inventoryReport.product')}</TableHead>
                  <TableHead className="text-right">
                    {t('inventoryReport.moved')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('inventoryReport.movedValue')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('inventoryReport.onHand')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('inventoryReport.onHandValue')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={`${row.gtin}-${row.name}`}>
                    <TableCell>
                      <div className="min-w-0">
                        <p className="truncate font-medium">{rowName(row)}</p>
                        <p className="text-muted-foreground truncate font-mono text-xs">
                          {row.gtin}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatNumber(row.movementAmount)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.movementValue)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNumber(row.totalQuantityOnHand)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-right tabular-nums">
                      {formatCurrency(row.totalQuantityOnHandValue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative lg:hidden">
            <BusyOverlay show={report.isFetching} />
            <div
              className={cn(
                'space-y-2',
                report.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((row) => (
                <div
                  key={`${row.gtin}-${row.name}`}
                  className="bg-card space-y-2 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{rowName(row)}</p>
                      <p className="text-muted-foreground truncate font-mono text-xs">
                        {row.gtin}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-medium tabular-nums">
                        {formatNumber(row.movementAmount)}
                      </p>
                      <p className="text-muted-foreground text-xs tabular-nums">
                        {formatCurrency(row.movementValue)}
                      </p>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {t('inventoryReport.onHandRow', {
                      quantity: formatNumber(row.totalQuantityOnHand),
                      value: formatCurrency(row.totalQuantityOnHandValue),
                    })}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <Pagination
            page={page}
            size={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
